import { useEffect, useRef, useState } from 'react';

export default function Testimonials() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  const testimonials = [
    {
      name: 'Marcus Johnson',
      location: 'Dallas, TX',
      vehicle: '2023 Ford F-150 XLT',
      rating: 5,
      text: 'I needed a reliable work truck fast and Paramount Motors delivered — literally. Found the right F-150, got financing approved quickly, and it was shipped to my door within two weeks. Couldn\'t be easier.',
      avatar: 'https://public.readdy.ai/ai/img_res/75397f4c25946e1ed69debb3362e6a0b.jpg',
    },
    {
      name: 'Jennifer Castillo',
      location: 'Phoenix, AZ',
      vehicle: '2022 Toyota Highlander',
      rating: 5,
      text: 'We needed a family SUV with good mileage and a fair price. Paramount had exactly what we were looking for. The team was patient, answered all our questions, and the whole process was stress-free.',
      avatar: 'https://readdy.ai/api/search-image?query=friendly%20latina%20woman%20portrait%20warm%20smile%20natural%20light%20casual%20attire%20approachable%20everyday%20person%20headshot&width=200&height=200&seq=pm_avatar_suv_buyer&orientation=squarish',
    },
    {
      name: 'Tyler Brooks',
      location: 'Columbus, OH',
      vehicle: '2021 Honda Accord Sport',
      rating: 5,
      text: 'First time buying a car online and I was nervous, but Paramount made it simple. Great price on a clean Accord, easy financing, and it was shipped to Ohio without any issues. Will definitely use them again.',
      avatar: 'https://readdy.ai/api/search-image?query=friendly%20young%20white%20man%20portrait%20casual%20smile%20natural%20light%20approachable%20everyday%20person%20headshot&width=200&height=200&seq=pm_avatar_sedan_buyer&orientation=squarish',
    },
  ];

  return (
    <section ref={sectionRef} className="py-20 sm:py-28 px-4 sm:px-8 lg:px-16 bg-[#111111]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-12 bg-[#C9A84C]"></div>
            <span className="text-[#C9A84C] text-xs tracking-[0.4em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Client Stories
            </span>
            <div className="h-px w-12 bg-[#C9A84C]"></div>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            What Our Clients Say
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto text-sm leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
            Don't just take our word for it. Hear from clients who've experienced the Paramount difference.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.name}
              className={`bg-[#0d0d0d] border border-white/5 p-6 rounded-sm hover:border-[#C9A84C]/40 transition-all duration-700 hover:shadow-xl hover:shadow-[#C9A84C]/5 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${300 + index * 150}ms` }}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#C9A84C] text-sm"></i>
                ))}
              </div>

              {/* Quote */}
              <p className="text-white/70 text-sm leading-relaxed mb-6" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                  <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {testimonial.name}
                  </p>
                  <p className="text-white/60 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {testimonial.location}
                  </p>
                  <p className="text-[#C9A84C] text-xs mt-0.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {testimonial.vehicle}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}